import { useContext, useEffect, useRef, useState } from 'react';

import { ImageContext } from '@contexts/image';
import { VideoContext } from '@contexts/video';
import { SkuContext } from '@contexts/sku';

import AxiosInstanceGenerator from '@src/utils/axiosInstance';

import QuestionMarkPopup from '@components/popups/QuestionMark';
import * as CenterDiamondIllustrationContent from '@components/popups/content/CenterDiamondIllustration';

import * as routes from '@src/app_routes/settings';

import { handleError } from '../../../utils/errors';

import css from '@styles/productDetail';

declare var noUiSlider: any;

export default function StoneSizeSlider() {
  const { sku, setSku } = useContext(SkuContext);
  const { setImage: setMainImage } = useContext(ImageContext);
  const { clearVideo } = useContext(VideoContext);
  const [images, setImages] = useState(null);
  const [sliderInitialized, setSliderInitialized] = useState(false);
  const sliderElRef = useRef(null);
  const sliderRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (!sku) return;
    if (images && images.length) return;

    AxiosInstanceGenerator()
      .get(routes.apiSettingSizeImages(sku))
      .then(res => {
        const applicableResults = res.data.filter(
          d => parseFloat(d.approximate_stone_weight) >= 0.25
        );
        setImages(applicableResults);
      })
      .catch(handleError);
  }, [images, sku]);

  useEffect(() => {
    if (!(images && images.length)) return;
    if (!sliderElRef.current) return;

    const currentImageIndex = images.findIndex(
      image => image.vendor_sku === sku
    );
    const sliderOpts = {
      start: [currentImageIndex],
      keyboardSupport: false,
      range: {
        min: 0,
        max: images.length - 1
      },
      step: 1,
      connect: [false, false]
    };

    sliderRef.current = noUiSlider.create(sliderElRef.current, sliderOpts);

    setSliderInitialized(true);

    return () => sliderRef.current.destroy();
  }, [images, sku]);

  useEffect(() => {
    if (!sliderRef.current) return;

    sliderRef.current.off('update');
    sliderRef.current.off('change');

    sliderRef.current.on('update', (_values, _handle, unencoded) => {
      const sel = images[Math.round(unencoded[0])];
      setSelectedImage(sel);
      setMainImage(sel.image);
      clearVideo(); // Clear video context just in case we were showing a video
    });

    sliderRef.current.on('change', (_values, _handle, unencoded) => {
      const newSku = images[Math.round(unencoded[0])].vendor_sku;
      if (sku !== newSku) {
        setSku(newSku);
      }
    });
  }, [clearVideo, images, setMainImage, setSku, sku, sliderInitialized]);

  return (
    <div className={css.stoneSizeSlider}>
      {selectedImage && (
        <div
          style={{
            marginBottom: 11,
            textAlign: 'center'
          }}
        >
          How a{' '}
          <strong>
            {Number(selectedImage.approximate_stone_weight).toFixed(2)} ct.
          </strong>{' '}
          center diamond will look.
          <QuestionMarkPopup
            initialContent={CenterDiamondIllustrationContent}
          />
        </div>
      )}
      <MemoizedStoneSizeSlider ref={sliderElRef} />
    </div>
  );
}

const MemoizedStoneSizeSlider = React.memo(
  React.forwardRef(function(_props, ref) {
    return <div ref={ref} className='oversized-handles' />;
  })
);
